import { isAxiosError } from 'axios';

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isDemoModeWriteError } from '~modules/product-demo/product-demo-error';
import { openProductDemoBlockedModal } from '~modules/product-demo/product-demo-modal';

import { dispatchStoreAction } from '~system/store/store-accessor';
import { triggerToast } from '~system/store/toast/toast-slice';

const GLOBAL_ERROR_TOAST = {
  title: 'Сталася помилка',
  message: 'Щось пішло не так. Спробуйте ще раз.',
} as const;

const GLOBAL_ERROR_TOAST_COOLDOWN_MS = 1500;

let lastGlobalErrorToastAt = 0;

const shouldShowGlobalErrorToast = (error: unknown) => {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;

  return status !== 401 && status !== 422;
};

const showGlobalErrorToast = (error: unknown) => {
  if (!shouldShowGlobalErrorToast(error)) return;

  const now = Date.now();

  if (now - lastGlobalErrorToastAt < GLOBAL_ERROR_TOAST_COOLDOWN_MS) return;

  lastGlobalErrorToastAt = now;

  dispatchStoreAction(
    triggerToast({
      ...GLOBAL_ERROR_TOAST,
      type: 'error',
    }),
  );
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.options.meta?.skipGlobalErrorToast) return;

      showGlobalErrorToast(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isDemoModeWriteError(error)) {
        openProductDemoBlockedModal();
        return;
      }

      if (mutation.options.meta?.skipGlobalErrorToast) return;

      showGlobalErrorToast(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,
      retry: 1,
      retryDelay: (attemptIndex) => {
        return Math.min(1000 * 2 ** attemptIndex, 5000);
      },
    },
  },
});
